import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { supabase } from '@/services/supabaseClient'
import PageShell from '@/layout/PageShell'
import { fetchAllRows, resolveSubjectRule } from '@/services/examResultContext'

const TABLE_NAME = 'FMHS_exam_data'

interface GradingCriteria {
  cqPass: number; mcqPass: number; practicalPass: number; totalPass: number
  gradeAPlus: number; gradeA: number; gradeAMinus: number; gradeB: number; gradeC: number; gradeD: number
}

const defaultCriteria: GradingCriteria = {
  cqPass: 16, mcqPass: 8, practicalPass: 8, totalPass: 33,
  gradeAPlus: 80, gradeA: 70, gradeAMinus: 60, gradeB: 50, gradeC: 40, gradeD: 33,
}

const CRITERIA_LABELS: Record<keyof GradingCriteria, string> = {
  cqPass: 'CQ Pass', mcqPass: 'MCQ Pass', practicalPass: 'Practical Pass', totalPass: 'Total Pass',
  gradeAPlus: 'A+ (5.0)', gradeA: 'A (4.0)', gradeAMinus: 'A- (3.5)',
  gradeB: 'B (3.0)', gradeC: 'C (2.0)', gradeD: 'D (1.0)',
}

interface SubjectComponentMap { CQ?: string; MCQ?: string; PRACTICAL?: string; TOTAL?: string; GPA?: string }
type SubjectType = { type: 'single'; components: SubjectComponentMap } | { type: 'combined'; subjects: { base: string; components: SubjectComponentMap }[] }

interface DataRow {
  id: number
  exam_id: number | null
  iid: string
  class: number | null
  section: string | null
  cq: number; mcq: number; practical: number; total: number
  gpa: number | string | null
  _db_gpa: number | string | null   // DB snapshot for dirty detection
  originalRow: Record<string, unknown>
}

interface SubjectDisplayCols {
  cqCol: string; mcqCol: string; practicalCol: string; totalCol: string; gpaCol: string
  isCombined: boolean
}

function calculateGPA(total: number, criteria: GradingCriteria, fullMarks: number = 100): number {

  if (total <= 0) return 0
  const normalizedTotal = (total / fullMarks) * 100
  let base = 0
  if (normalizedTotal >= criteria.gradeAPlus) base = 5
  else if (normalizedTotal >= criteria.gradeA) base = 4
  else if (normalizedTotal >= criteria.gradeAMinus) base = 3.5
  else if (normalizedTotal >= criteria.gradeB) base = 3
  else if (normalizedTotal >= criteria.gradeC) base = 2
  else if (normalizedTotal >= criteria.gradeD) base = 1
  return base
}

function isGpaDirty(row: DataRow): boolean {
  const norm = (v: number | string | null) => v === 'F' ? 'F' : (v == null ? null : Number(v))
  const a = norm(row.gpa), b = norm(row._db_gpa)
  if (a === 'F' || b === 'F') return a !== b
  if (a == null && b == null) return false
  if (a == null || b == null) return true
  return Math.abs(Number(a) - Number(b)) > 0.001
}

export default function SubjectGpaPage() {
  const { examId } = useParams()
  const navigate = useNavigate()
  const hasExamParam = examId !== undefined
  const parsedExamId = hasExamParam ? Number(examId) : null
  const invalidExamParam = hasExamParam && !Number.isFinite(parsedExamId ?? NaN)
  const activeExamId = !invalidExamParam && hasExamParam ? parsedExamId : null
  const [subjects, setSubjects] = useState<Map<string, SubjectType>>(new Map())
  const [selectedSubject, setSelectedSubject] = useState('')
  const [criteria, setCriteria] = useState<GradingCriteria>(defaultCriteria)
  const [data, setData] = useState<DataRow[]>([])
  const [displayCols, setDisplayCols] = useState<SubjectDisplayCols | null>(null)
  const [iidCol, setIidCol] = useState('iid')
  const [status, setStatus] = useState('')
  const [loading, setLoading] = useState(false)
  const [rowSaved, setRowSaved] = useState<Record<number, boolean>>({})
  const [subjectRules, setSubjectRules] = useState<any[]>([])
  const [combinedMode, setCombinedMode] = useState(true) // true = 1st+2nd together, false = separate
  const [availableClasses, setAvailableClasses] = useState<number[]>([])
  const [selectedClasses, setSelectedClasses] = useState<number[]>([])

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user) { navigate('/login', { replace: true }); return }
      initSubjects()
    })
  }, [navigate])

  // Re-run initSubjects when combinedMode changes
  useEffect(() => { initSubjects() }, [combinedMode])

  async function initSubjects() {
    setStatus('Loading subjects…')
    if (activeExamId !== null) {
      const { data: rules } = await supabase.from('FMHS_exam_subjects').select('*').eq('exam_id', activeExamId)
      setSubjectRules(rules || [])

      const { data: clsRows } = await supabase.from(TABLE_NAME).select('class').eq('exam_id', activeExamId)
      if (clsRows) {
        const uniq = [...new Set(clsRows.map(r => Number(r.class)).filter(n => !isNaN(n) && n > 0))].sort((a, b) => a - b)
        setAvailableClasses(uniq)
      }
    }
    const { data: rows, error } = await supabase.from(TABLE_NAME).select('*').limit(1)
    if (error || !rows?.length) { setStatus('Error loading table: ' + error?.message); return }
    const cols = Object.keys(rows[0])
    const detectedIid = cols.find(k => /^iid$/i.test(k)) ?? cols.find(k => /iid/i.test(k)) ?? 'iid'
    setIidCol(detectedIid)

    const raw = new Map<string, SubjectComponentMap>()
    cols.forEach(col => {
      const m = col.match(/^(.+?)_(CQ|MCQ|Practical|Total|GPA)$/i)
      if (m) {
        const base = m[1].trim()
        const comp = m[2].toUpperCase() as keyof SubjectComponentMap
        if (!raw.has(base)) raw.set(base, {})
        raw.get(base)![comp] = col
      }
    })

    const processed = new Map<string, SubjectType>()
    const paired = new Set<string>()

    // Sort bases to ensure consistent grouping
    const sortedBases = Array.from(raw.keys()).sort()

    sortedBases.forEach(base => {
      if (paired.has(base)) return

      if (combinedMode && /1st/i.test(base)) {
        const baseWithout1st = base.replace(/1st/i, '').trim()
        const secondPaperBase = sortedBases.find(b => b !== base && b.replace(/2nd/i, '').trim() === baseWithout1st)
        
        if (secondPaperBase) {
          const key = base.replace(/1st/i, '1st+2nd').trim()
          processed.set(key, { type: 'combined', subjects: [
            { base, components: raw.get(base)! },
            { base: secondPaperBase, components: raw.get(secondPaperBase)! }
          ]})
          paired.add(base)
          paired.add(secondPaperBase)
          return
        }
      }
      
      // If not paired yet and not a "2nd" paper that belongs to a pair
      if (!paired.has(base)) {
        processed.set(base, { type: 'single', components: raw.get(base)! })
      }
    })

    setSubjects(processed)
    setStatus(`Found ${processed.size} subjects`)
  }

  const loadSubjectData = useCallback(async (subject: string) => {
    if (!subject) return
    if (invalidExamParam) {
      setStatus(`Invalid exam id in URL: ${examId}`)
      setData([])
      setDisplayCols(null)
      setRowSaved({})
      setLoading(false)
      return
    }

    setLoading(true); setStatus('Loading…')
    const rows = await fetchAllRows<Record<string, unknown>>(async (from, to) => {
      let pageQuery = supabase.from(TABLE_NAME).select('*')
      if (activeExamId !== null) {
        pageQuery = pageQuery.eq('exam_id', activeExamId)
      }
      const res = await pageQuery.order(iidCol, { ascending: true }).range(from, to)
      return res as any
    })


    const subjInfo = subjects.get(subject)!
    const parsed: DataRow[] = []

    if (subjInfo.type === 'single') {
      const c = subjInfo.components
      setDisplayCols({
        cqCol: c.CQ ?? '', mcqCol: c.MCQ ?? '', practicalCol: c.PRACTICAL ?? '',
        totalCol: c.TOTAL ?? '', gpaCol: c.GPA ?? '', isCombined: false,
      })
      ;(rows ?? []).forEach(row => {
        const clsNum = Number(row.class || row.Class) || null
        const secStr = String(row.section || row.Section || '')

        if (selectedClasses.length > 0 && clsNum !== null && !selectedClasses.includes(clsNum)) {
          return
        }

        const cq = Number(row[c.CQ!]) || 0, mcq = Number(row[c.MCQ!]) || 0
        const practical = Number(row[c.PRACTICAL!]) || 0, total = Number(row[c.TOTAL!]) || 0
        const gpaRaw = c.GPA ? row[c.GPA] : null
        const hasAny = cq > 0 || mcq > 0 || practical > 0 || total > 0 || (typeof gpaRaw === 'string' && gpaRaw.trim().toUpperCase() === 'F')
        if (!hasAny) return
        parsed.push({
          id: Number(row.id),
          exam_id: row.exam_id as number | null,
          iid: String(row[iidCol] ?? ''),
          class: clsNum,
          section: secStr,
          cq, mcq, practical, total,
          gpa: gpaRaw as string | null, _db_gpa: gpaRaw as string | null,
          originalRow: row as Record<string, unknown>,
        })
      })
    } else {
      const subs = subjInfo.subjects
      const firstPaper = subs.find(s => /1st/i.test(s.base)) ?? subs[0]
      setDisplayCols({
        cqCol: '', mcqCol: '', practicalCol: '', totalCol: '',
        gpaCol: firstPaper?.components.GPA ?? '', isCombined: true,
      })
      ;(rows ?? []).forEach(row => {
        if (subs.length < 2) return
        const clsNum = Number(row.class || row.Class) || null
        const secStr = String(row.section || row.Section || '')

        if (selectedClasses.length > 0 && clsNum !== null && !selectedClasses.includes(clsNum)) {
          return
        }

        const t1 = Number(row[subs[0].components.TOTAL!]) || 0
        const t2 = Number(row[subs[1].components.TOTAL!]) || 0
        
        let combinedFullMarks = 100 // default fallback
        if (subjectRules.length > 0) {
          const rule1 = resolveSubjectRule(subjectRules as any[], subs[0].base)
          const rule2 = resolveSubjectRule(subjectRules as any[], subs[1].base)
          if (rule1 && rule2) combinedFullMarks = rule1.full_marks + rule2.full_marks
          else combinedFullMarks = 150
        } else {
          combinedFullMarks = 150
        }
        
        const convertedTotal = Math.round(((t1 + t2) / combinedFullMarks) * 100 * 100) / 100
        const gpaRaw = firstPaper.components.GPA ? row[firstPaper.components.GPA] : null
        const hasGpa = (typeof gpaRaw === 'string' && gpaRaw.trim().toUpperCase() === 'F') || Number(gpaRaw) > 0
        if (!hasGpa && t1 <= 0 && t2 <= 0) return
        parsed.push({
          id: Number(row.id),
          exam_id: row.exam_id as number | null,
          iid: String(row[iidCol] ?? ''),
          class: clsNum,
          section: secStr,
          cq: 0, mcq: 0, practical: 0, total: convertedTotal,
          gpa: gpaRaw as string | null, _db_gpa: gpaRaw as string | null,
          originalRow: row as Record<string, unknown>,
        })
      })
    }

    setData(parsed)
    setRowSaved({})
    setStatus(activeExamId !== null ? `Loaded ${parsed.length} records for exam ${activeExamId}` : `Loaded ${parsed.length} records`)
    setLoading(false)
  }, [subjects, iidCol, activeExamId, invalidExamParam, examId, selectedClasses, subjectRules])

  useEffect(() => {
    if (selectedSubject) loadSubjectData(selectedSubject)
  }, [selectedSubject, loadSubjectData])

  function calculateAll() {
    const subjInfo = subjects.get(selectedSubject)
    const isCombined = subjInfo?.type === 'combined'
    const updated = data.map(row => {
      const summed = row.cq + row.mcq + row.practical
      const total = summed > 0 ? summed : row.total
      let hasFailed = false
      if (!isCombined) {
        if (row.cq > 0 && row.cq < criteria.cqPass) hasFailed = true
        if (row.mcq > 0 && row.mcq < criteria.mcqPass) hasFailed = true
        if (row.practical > 0 && row.practical < criteria.practicalPass) hasFailed = true
      }
      if (total > 0 && total < criteria.totalPass) hasFailed = true
      if (total > 100) hasFailed = true

      let gpa: number | string | null = null
      if (total === 0) { gpa = null }
      else if (hasFailed) { gpa = 'F' }
      else { 
        let fullMarks = 100
        if (isCombined) {
           const subs = (subjInfo as Extract<SubjectType, { type: 'combined' }>).subjects
            const rule1 = resolveSubjectRule(subjectRules as any[], subs[0].base)
            const rule2 = resolveSubjectRule(subjectRules as any[], subs[1].base)
           if (rule1 && rule2) fullMarks = rule1.full_marks + rule2.full_marks
        } else {
            const rule = resolveSubjectRule(subjectRules as any[], selectedSubject)
           if (rule) fullMarks = rule.full_marks
        }
        gpa = calculateGPA(total, criteria, fullMarks) 
      }
      return { ...row, total, gpa }
    })
    setData(updated)
    setStatus(`✅ Calculation completed! — click "Bulk Update to Database" to save`)
  }

  async function bulkUpdate() {
    if (!window.confirm(`Update ${data.length} records to database?`)) return
    if (invalidExamParam) {
      setStatus(`Invalid exam id in URL: ${examId}`)
      return
    }
    setStatus('Bulk updating…')
    const subjInfo = subjects.get(selectedSubject)
    if (!subjInfo) return
    const updates = data.map(row => {
      const u: Record<string, unknown> = { id: row.id, exam_id: row.exam_id ?? activeExamId, iid: row.iid }
      if (subjInfo.type === 'single') {
        const c = subjInfo.components
        if (c.TOTAL) u[c.TOTAL] = row.total || 0
        if (c.GPA) u[c.GPA] = row.gpa === 'F' ? 'F' : (row.gpa == null ? null : Number(row.gpa))
      } else {
        const fp = (subjInfo as Extract<SubjectType, { type: 'combined' }>).subjects.find(s => /1st/i.test(s.base)) ?? subjInfo.subjects[0]
        if (fp.components.GPA) u[fp.components.GPA] = row.gpa === 'F' ? 'F' : (row.gpa == null ? null : Number(row.gpa))
      }
      return u
    })
    const { error } = await supabase.from(TABLE_NAME).upsert(updates, { onConflict: 'id' })
    if (!error) {
      setData(prev => prev.map(r => ({ ...r, _db_gpa: r.gpa })))
      setStatus(`✅ Bulk update completed: ${data.length} rows updated successfully!`)
    } else {
      setStatus('Error: ' + error.message)
    }
  }

  async function updateSingleRow(rowId: number) {
    if (invalidExamParam) {
      setStatus(`Invalid exam id in URL: ${examId}`)
      return
    }
    const row = data.find(item => item.id === rowId)
    const subjInfo = subjects.get(selectedSubject)
    if (!subjInfo || !row) return
    const u: Record<string, unknown> = {}
    if (subjInfo.type === 'single') {
      const c = subjInfo.components
      if (c.TOTAL) u[c.TOTAL] = row.total || 0
      if (c.GPA) u[c.GPA] = row.gpa === 'F' ? 'F' : (row.gpa == null ? null : Number(row.gpa))
    } else {
      const fp = (subjInfo as Extract<SubjectType, { type: 'combined' }>).subjects.find(s => /1st/i.test(s.base)) ?? subjInfo.subjects[0]
      if (fp?.components.GPA) u[fp.components.GPA] = row.gpa === 'F' ? 'F' : (row.gpa == null ? null : Number(row.gpa))
    }
    const { error } = await supabase.from(TABLE_NAME).update(u).eq('id', row.id)
    if (!error) {
      setData(prev => prev.map(r => r.id === rowId ? { ...r, _db_gpa: r.gpa } : r))
      setRowSaved(prev => ({ ...prev, [rowId]: true }))
      setTimeout(() => setRowSaved(prev => ({ ...prev, [rowId]: false })), 2000)
      setStatus('Row updated successfully')
    }
  }

  const passMarkKeys: (keyof GradingCriteria)[] = ['cqPass', 'mcqPass', 'practicalPass', 'totalPass']
  const gpaKeys: (keyof GradingCriteria)[] = ['gradeAPlus', 'gradeA', 'gradeAMinus', 'gradeB', 'gradeC', 'gradeD']
  const thBase: React.CSSProperties = { padding: '6px 8px', background: '#f0f3f6', border: '1px solid #d0d7de', fontWeight: 600, fontSize: '12px', textAlign: 'center', verticalAlign: 'middle' }

  return (
    <PageShell
      title={activeExamId !== null ? `Part 3 – Subject GPA (Exam ${activeExamId})` : 'Part 3 – Subject GPA'}
      backHref={activeExamId !== null ? `/exam-panel/${activeExamId}` : '/dashboard'}
    >
      {() => (
        <div>
          <p style={{ fontSize: '14px', color: '#6a737d', marginBottom: '14px' }}>
            Subject-wise GPA Calculation — সাবজেক্ট অনুযায়ী GPA গণনা ও আপডেট
          </p>

          {/* Subject Selection + Mode Toggle */}
          <div className="card" style={{ marginBottom: '14px', background: '#f8fbff' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <div style={{ fontWeight: 600, color: '#495057' }}>📚 Subject Selection</div>
              {/* Combined / Separate toggle for 1st+2nd paper subjects */}
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center', fontSize: '12px' }}>
                <span style={{ color: '#555', fontWeight: 500 }}>১ম+২য় পত্র মোড:</span>
                <button
                  onClick={() => { setCombinedMode(true); setSelectedSubject(''); setData([]) }}
                  style={{
                    padding: '3px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 600,
                    border: '1px solid', borderColor: combinedMode ? '#0366d6' : '#ced4da',
                    background: combinedMode ? '#0366d6' : '#fff',
                    color: combinedMode ? '#fff' : '#555',
                  }}
                >একসাথে</button>
                <button
                  onClick={() => { setCombinedMode(false); setSelectedSubject(''); setData([]) }}
                  style={{
                    padding: '3px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 600,
                    border: '1px solid', borderColor: !combinedMode ? '#0366d6' : '#ced4da',
                    background: !combinedMode ? '#0366d6' : '#fff',
                    color: !combinedMode ? '#fff' : '#555',
                  }}
                >আলাদা আলাদা</button>
              </div>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: '#555', marginBottom: '4px' }}>Select Subject</label>
              <select value={selectedSubject} onChange={e => setSelectedSubject(e.target.value)} style={{ minWidth: '250px' }}>
                <option value="">Choose a subject…</option>
                {Array.from(subjects.keys()).sort().map(s => (
                  <option key={s} value={s}>{s.replace(/^\*+\s*/, '')}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Class Filter (Multiple Selection) */}
          {availableClasses.length > 0 && (
            <div className="card" style={{ marginBottom: '14px', background: '#fcfcfd', border: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <div style={{ fontWeight: 700, color: '#334155', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span>🏫</span> Class Selection (মাল্টিপল ক্লাস সিলেক্ট করতে পারবেন):
                </div>
                <button
                  onClick={() => setSelectedClasses([])}
                  style={{
                    padding: '3px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 700, cursor: 'pointer',
                    border: '1px solid #cbd5e1', background: selectedClasses.length === 0 ? '#4f46e5' : '#fff',
                    color: selectedClasses.length === 0 ? '#fff' : '#64748b'
                  }}
                >
                  All Classes ({availableClasses.length})
                </button>
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {availableClasses.map(cls => {
                  const isSelected = selectedClasses.length === 0 || selectedClasses.includes(cls)
                  return (
                    <button
                      key={cls}
                      onClick={() => {
                        setSelectedClasses(prev => {
                          if (prev.length === 0) {
                            return [cls]
                          }
                          if (prev.includes(cls)) {
                            return prev.filter(c => c !== cls)
                          } else {
                            return [...prev, cls].sort((a, b) => a - b)
                          }
                        })
                      }}
                      style={{
                        padding: '6px 14px', borderRadius: '10px', cursor: 'pointer', fontSize: '12px', fontWeight: 800,
                        border: isSelected ? '1.5px solid #4f46e5' : '1px solid #cbd5e1',
                        background: isSelected ? '#eeeffe' : '#fff',
                        color: isSelected ? '#4338ca' : '#64748b',
                        display: 'flex', alignItems: 'center', gap: '6px', transition: 'all 0.15s'
                      }}
                    >
                      <span>{isSelected ? '☑' : '☐'}</span> Class {cls}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* Manual Grading Criteria */}
          <div className="card" style={{ marginBottom: '14px', background: '#f8f9fa' }}>
            <div style={{ fontWeight: 600, marginBottom: '10px', color: '#495057' }}>⚙️ Manual Grading Criteria</div>
            <div style={{ marginBottom: '10px' }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#495057', marginBottom: '6px' }}>Pass Marks</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                {passMarkKeys.map(key => (
                  <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <label style={{ fontSize: '12px', color: '#495057', fontWeight: 500 }}>{CRITERIA_LABELS[key]}:</label>
                    <input type="number" value={criteria[key]} min={0} max={100}
                      onChange={e => setCriteria(prev => ({ ...prev, [key]: Number(e.target.value) }))}
                      style={{ width: '50px', padding: '4px', border: '1px solid #ced4da', borderRadius: '3px', fontSize: '12px' }} />
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#495057', marginBottom: '6px' }}>GPA Thresholds</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                {gpaKeys.map(key => (
                  <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <label style={{ fontSize: '12px', color: '#495057', fontWeight: 500 }}>{CRITERIA_LABELS[key]}:</label>
                    <input type="number" value={criteria[key]} min={0} max={100}
                      onChange={e => setCriteria(prev => ({ ...prev, [key]: Number(e.target.value) }))}
                      style={{ width: '50px', padding: '4px', border: '1px solid #ced4da', borderRadius: '3px', fontSize: '12px' }} />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="card" style={{ marginBottom: '14px', background: '#f0fff0' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', alignItems: 'center' }}>
              <button
                className="btn"
                style={{ background: '#ff8a00', borderColor: '#ff8a00', color: '#fff' }}
                onClick={calculateAll} disabled={data.length === 0 || loading}
              >
                🧮 Calculate All GPA
              </button>
              <button className="btn btn-success" onClick={bulkUpdate} disabled={data.length === 0 || loading}>
                💾 Bulk Update to Database
              </button>
              {status && <span style={{ fontSize: '13px', color: status.startsWith('✅') ? '#1a7f37' : status.startsWith('Error') ? '#d73a49' : '#555' }}>{status}</span>}
            </div>
          </div>

          {/* Data Table */}
          {loading && <div className="spinner" />}
          {!loading && data.length > 0 && displayCols && (
            <div style={{ background: '#fff', border: '1px solid #d0d7de', borderRadius: '6px' }}>
              <div style={{ padding: '10px 14px', background: '#f8f9fa', borderBottom: '1px solid #d0d7de', fontWeight: 600, fontSize: '14px', color: '#495057' }}>
                📊 Data for {selectedSubject.replace(/^\*+\s*/, '')}
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', fontSize: '13.5px', width: '100%', border: '1px solid #d0d7de' }}>
                  <thead>
                    <tr>
                      <th style={thBase}>IID</th>
                      <th style={thBase}>Class</th>
                      <th style={thBase}>Sec</th>
                      {!displayCols.isCombined && displayCols.cqCol && <th style={thBase}>{displayCols.cqCol}</th>}
                      {!displayCols.isCombined && displayCols.mcqCol && <th style={thBase}>{displayCols.mcqCol}</th>}
                      {!displayCols.isCombined && displayCols.practicalCol && <th style={thBase}>{displayCols.practicalCol}</th>}
                      {displayCols.totalCol && <th style={thBase}>{displayCols.totalCol}</th>}
                      <th style={thBase}>{displayCols.gpaCol || 'GPA'}</th>
                      <th style={thBase}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((row, i) => {
                      const dirty = isGpaDirty(row)
                      const gpaDisplay = row.gpa === null ? '' : row.gpa === 'F' ? 'F' : Number(row.gpa).toFixed(2)
                      return (
                        <tr key={row.id} style={{ background: i % 2 === 0 ? '#fff' : '#fcfcfd' }}>
                          <td style={{ padding: '6px 8px', border: '1px solid #d0d7de', textAlign: 'center' }}>{row.iid}</td>
                          <td style={{ padding: '6px 8px', border: '1px solid #d0d7de', textAlign: 'center', fontWeight: 700, color: '#334155' }}>
                            {row.class ? `Class ${row.class}` : '-'}
                          </td>
                          <td style={{ padding: '6px 8px', border: '1px solid #d0d7de', textAlign: 'center', color: '#64748b', fontSize: '12px' }}>
                            {row.section || '-'}
                          </td>
                          {!displayCols.isCombined && displayCols.cqCol && (
                            <td style={{ padding: '6px 8px', border: '1px solid #d0d7de', textAlign: 'center' }}>{row.cq || ''}</td>
                          )}
                          {!displayCols.isCombined && displayCols.mcqCol && (
                            <td style={{ padding: '6px 8px', border: '1px solid #d0d7de', textAlign: 'center' }}>{row.mcq || ''}</td>
                          )}
                          {!displayCols.isCombined && displayCols.practicalCol && (
                            <td style={{ padding: '6px 8px', border: '1px solid #d0d7de', textAlign: 'center' }}>{row.practical || ''}</td>
                          )}
                          {displayCols.totalCol && (
                            <td style={{ padding: '6px 8px', border: '1px solid #d0d7de', textAlign: 'center' }}>{row.total || ''}</td>
                          )}
                          <td style={{ padding: '6px 8px', border: '1px solid #d0d7de', textAlign: 'center', fontWeight: 600, color: row.gpa === 'F' ? '#d73a49' : '#ff6600' }}>
                            {gpaDisplay}
                          </td>
                          <td style={{ padding: '6px 8px', border: '1px solid #d0d7de', textAlign: 'center' }}>
                            <button
                              onClick={() => updateSingleRow(row.id)}
                              style={{
                                fontSize: '11px', padding: '3px 10px', border: '1px solid', borderRadius: '4px',
                                cursor: 'pointer', fontWeight: 500,
                                background: rowSaved[row.id] ? '#1a7f37' : dirty ? '#ff8a00' : '#0366d6',
                                borderColor: rowSaved[row.id] ? '#1a7f37' : dirty ? '#ff8a00' : '#0366d6',
                                color: '#fff',
                              }}
                            >
                              {rowSaved[row.id] ? '✅ Updated' : 'Update'}
                            </button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {!loading && data.length === 0 && selectedSubject && (
            <div className="card" style={{ textAlign: 'center', color: '#6a737d', padding: '30px' }}>No data for this subject</div>
          )}
        </div>
      )}
    </PageShell>
  )
}

